import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Milestone, CheckCircle2, Circle, Hourglass, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parse "YYYY-MM-DD" or human-readable dates into a Date.
function parseDate(raw) {
  if (!raw) return null;
  // Accept ISO and common human formats.
  const iso = /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw + 'T00:00:00Z' : raw;
  const d = new Date(iso);
  return isNaN(d) ? null : d;
}
function fmt(date) {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

// Map a per-milestone `status` (planned/in-progress/done/blocked) to the
// shared "Road to Reopening" visual vocabulary (done / active / upcoming).
function rowState(m, now) {
  if (m.status === 'done') return 'done';
  if (m.status === 'in-progress') return 'active';
  if (m.status === 'blocked') return 'active';
  const due = parseDate(m.due_date);
  if (due && due < now) return 'done';
  return 'upcoming';
}

/**
 * DepartmentRoadmapStrip — vertical milestone timeline anchored to the
 * bottom of each department screen. Mirrors the public "Road to
 * Reopening · Our Journey Back" layout so the visual rhythm is
 * identical across the public landing page and the department views.
 *
 * Each milestone tile carries its due-date as part of the title
 * (suffix format: "Soft Open · Feb 15, 2027"), matching the master
 * Departmental Roadmaps section.
 */
export default function DepartmentRoadmapStrip({ deptId, deptLabel, onOpenEditor }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deptId || !adminToken) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/departments/${deptId}/roadmap-rollup`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data }) => {
        if (cancelled) return;
        const flat = [];
        for (const m of (data.parent?.milestones || [])) {
          flat.push({ ...m, source: 'parent' });
        }
        for (const c of (data.children || [])) {
          for (const m of (c.milestones || [])) {
            flat.push({ ...m, source: c.child_label || c.dept_id });
          }
        }
        // Sort by due_date ascending so the timeline reads chronologically.
        flat.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''));
        setRows(flat);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deptId, adminToken]);

  const now = useMemo(() => new Date(), []);
  const counts = useMemo(() => {
    let done = 0, active = 0, upcoming = 0;
    rows.forEach((m) => {
      const s = rowState(m, now);
      if (s === 'done') done++;
      else if (s === 'active') active++;
      else upcoming++;
    });
    return { done, active, upcoming };
  }, [rows, now]);

  if (loading) return null;

  return (
    <section
      data-testid="department-roadmap-strip"
      className="mt-8 rounded-2xl overflow-hidden"
      style={{ backgroundColor: '#F4F1EB', border: '1px solid #E8DFC6' }}
    >
      {/* Header — same visual vocabulary as the public Road to Reopening
          section: kicker badge, Cormorant Garamond H2, counter line. */}
      <div className="px-5 sm:px-8 pt-8 pb-4 text-center">
        <p className="text-xs uppercase tracking-[0.2em] font-bold mb-2" style={{ color: '#FF6F59', fontFamily: 'Manrope' }}>
          <Milestone className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {deptLabel} · Road to Reopening
        </p>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
          Our Journey Back
        </h2>
        <p className="mt-3 text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
          {counts.done} milestones complete · {counts.active} in progress · {counts.upcoming} upcoming
        </p>
        {onOpenEditor && (
          <button
            data-testid="department-roadmap-strip-edit"
            onClick={onOpenEditor}
            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold transition"
            style={{ backgroundColor: '#0A1A2F', color: 'white', fontFamily: 'Manrope' }}
          >
            Open editor <ChevronRight className="w-3 h-3" />
          </button>
        )}
        <p className="mt-2 text-[11px] italic" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          Tap any milestone to view the full description and linked tasks.
        </p>
      </div>

      {/* Body — empty state or vertical timeline */}
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
          <p className="text-xs">No milestones yet. Click <strong>Open editor</strong> and draft the first ones with AI or by hand.</p>
        </div>
      ) : (
        <div className="px-4 sm:px-8 pb-10">
          <div className="relative max-w-4xl mx-auto">
            {/* Vertical guide line */}
            <div className="absolute left-6 sm:left-1/2 top-0 bottom-0 w-px sm:-translate-x-1/2" style={{ backgroundColor: '#A3D4D4' }} />
            <div className="space-y-6">
              {rows.map((m, idx) => {
                const state = rowState(m, now);
                const Icon = state === 'done' ? CheckCircle2 : state === 'upcoming' ? Hourglass : Circle;
                const colors = state === 'done'
                  ? { bg: '#22C55E', label: 'COMPLETED' }
                  : state === 'active'
                    ? { bg: '#EAB308', label: 'IN PROGRESS' }
                    : { bg: '#A3D4D4', label: 'UPCOMING' };
                const leftSide = idx % 2 === 0;
                const due = parseDate(m.due_date);
                // Build the "Name · Date" suffix title. If the milestone
                // title already carries a date (e.g. seeded ones), don't
                // double-up — show as-is.
                const hasDateInTitle = /·|\b(20\d{2})\b/.test(m.title || '');
                const displayTitle = hasDateInTitle || !due
                  ? (m.title || '(untitled)')
                  : `${m.title} · ${fmt(due)}`;

                return (
                  <div key={`${m.id || idx}-${idx}`} className="relative flex items-start sm:items-center" data-testid={`dept-milestone-${m.id || idx}`}>
                    <div className="absolute left-6 sm:left-1/2 sm:-translate-x-1/2 w-5 h-5 rounded-full flex items-center justify-center ring-4" style={{ backgroundColor: colors.bg, top: '12px', boxShadow: '0 0 0 4px #F4F1EB' }}>
                      <Icon className="w-3 h-3 text-white" />
                    </div>

                    <div className={`ml-14 sm:ml-0 sm:w-1/2 ${leftSide ? 'sm:pr-10 sm:text-right' : 'sm:pl-10 sm:ml-auto'}`}>
                      <div
                        data-testid={`dept-milestone-card-${m.id || idx}`}
                        className="inline-block max-w-full text-left p-4 rounded-2xl"
                        style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.bg}20`, color: colors.bg, fontFamily: 'Manrope' }}>{colors.label}</span>
                          {m.source && m.source !== 'parent' && (
                            <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                              via {m.source}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg sm:text-xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>
                          {displayTitle}
                        </h3>
                        {m.description && (
                          <p className="text-[11px] mt-1 leading-relaxed" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
                            {m.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
